import { useState } from "react";
import { Box, Flex, Text, Stack, HStack, Badge, Input, Button, Icon } from "@chakra-ui/react";
import { Search, Trash2, Eraser } from "lucide-react";
import { apiPost } from "@/lib/api";
import { ConfirmModal } from "@/components/ConfirmModal";
import { colors } from "@/theme/colors";

// Results list — history rows are visible but not selectable.
function CleanupResults({ results, selected, toggle }) {
  if (results.length === 0) {
    return <Text fontSize="sm" color={colors.textSecondary} data-testid="cleanup-empty">No people match that pattern.</Text>;
  }
  return (
    <Stack gap="0" border="1px solid" borderColor={colors.border} borderRadius="10px" overflow="hidden">
      {results.map((p, i) => (
        <Flex key={p.worker_id} as="label" gap="3" px="3" py="2.5" align="center"
          borderTop={i === 0 ? "none" : "1px solid"} borderColor={colors.border}
          cursor={p.has_history ? "not-allowed" : "pointer"} opacity={p.has_history ? 0.75 : 1}
          bg={selected.has(p.worker_id) ? colors.tealTint : "transparent"}
          data-testid={`cleanup-row-${p.worker_id}`}>
          <Box as="input" type="checkbox" checked={selected.has(p.worker_id)} disabled={p.has_history}
            onChange={() => toggle(p.worker_id)} accentColor={colors.teal} w="15px" h="15px"
            data-testid={`cleanup-check-${p.worker_id}`} />
          <Box flex="1" minW="0">
            <HStack gap="2" flexWrap="wrap">
              <Text fontSize="sm" fontWeight="600" color={colors.textPrimary}>{p.name || "(no name)"}</Text>
              <Badge bg={colors.cream} color={colors.textSecondary} borderRadius="full" px="2">{p.company}</Badge>
              <Badge bg={p.status === "active" ? colors.resolvedBg : colors.pendingBg}
                color={p.status === "active" ? colors.resolvedText : colors.pendingText} borderRadius="full" px="2" textTransform="capitalize">{p.status}</Badge>
            </HStack>
            <Text fontSize="xs" color={colors.textSecondary}>{p.email || "no email"}</Text>
            {p.has_history && (
              <HStack gap="1" mt="0.5">
                <Icon as={Eraser} boxSize="3" color={colors.pendingText} />
                <Text fontSize="xs" color={colors.pendingText} data-testid={`cleanup-history-${p.worker_id}`}>
                  Has history ({p.history}) — deactivate instead
                </Text>
              </HStack>
            )}
          </Box>
        </Flex>
      ))}
    </Stack>
  );
}

// Super-Admin bulk cleanup: find people by name/email pattern, remove the
// no-history ones (worker + membership together). History = not selectable.
export function BulkCleanup({ flash, reload }) {
  const [pattern, setPattern] = useState("");
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const search = async () => {
    setBusy(true);
    try {
      const res = await apiPost("/platform/people/search", { pattern: pattern.trim() });
      setResults(res.people);
      setSelected(new Set());
    } catch (e) { flash("error", e.message); } finally { setBusy(false); }
  };

  const toggle = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const eligible = (results || []).filter((p) => !p.has_history);
  const chosen = (results || []).filter((p) => selected.has(p.worker_id));

  const removeSelected = async () => {
    setBusy(true);
    try {
      const res = await apiPost("/platform/people/bulk-delete", { worker_ids: [...selected] });
      const removed = res.removed?.length || 0;
      const skipped = res.skipped?.length || 0;
      flash("success", `${removed} record${removed === 1 ? "" : "s"} removed${skipped ? `, ${skipped} skipped (history/not found)` : ""}.`);
      setConfirmOpen(false);
      await search();
      reload?.();
    } catch (e) { flash("error", e.message); } finally { setBusy(false); }
  };

  return (
    <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="12px" p="4" data-testid="bulk-cleanup">
      <Text fontWeight="700" color={colors.textPrimary}>Cleanup</Text>
      <Text fontSize="sm" color={colors.textSecondary} mb="3">
        Find and remove test/junk people across all companies. Only records with no history are eligible — anyone with submissions or sign-offs must be deactivated instead.
      </Text>
      <Flex gap="2" mb="3" flexWrap="wrap">
        <Input value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder="Name or email pattern, e.g. TEST_"
          maxW="320px" bg={colors.white} borderColor={colors.border}
          onKeyDown={(e) => e.key === "Enter" && pattern.trim().length >= 2 && search()}
          data-testid="cleanup-pattern-input" />
        <Button size="sm" onClick={search} loading={busy} disabled={pattern.trim().length < 2} data-testid="cleanup-search-btn">
          <Search size={15} /> Search
        </Button>
        {eligible.length > 0 && (
          <Button size="sm" variant="outline" onClick={() => setSelected(new Set(eligible.map((p) => p.worker_id)))} data-testid="cleanup-select-all-btn">
            Select all eligible ({eligible.length})
          </Button>
        )}
        {selected.size > 0 && (
          <Button size="sm" bg={colors.poppy} color="white" _hover={{ opacity: 0.9 }} onClick={() => setConfirmOpen(true)} data-testid="cleanup-remove-btn">
            <Trash2 size={15} /> Remove selected ({selected.size})
          </Button>
        )}
      </Flex>

      {results && <CleanupResults results={results} selected={selected} toggle={toggle} />}

      <ConfirmModal open={confirmOpen} onClose={() => setConfirmOpen(false)}
        title={`Remove ${chosen.length} record${chosen.length === 1 ? "" : "s"} permanently?`}
        body={`This removes each person's worker record AND login together, and cannot be undone:\n\n${chosen.map((p) => `• ${p.name || p.email} (${p.company})`).join("\n")}`}
        confirmLabel="Remove selected" danger busy={busy} onConfirm={removeSelected} testId="cleanup-confirm-modal" />
    </Box>
  );
}
